import React, { useState, useEffect } from "react";
import ContentLoader from "react-content-loader";
import { toast, ToastContainer } from "react-toastify";
import { Building2, Plus } from "lucide-react";
import Tooltip from "@tippyjs/react";
import MaterialTable from 'material-table';
import "tippy.js/dist/tippy.css";
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';



import { getTrans as t } from "../../Localization/translations";
import subcompanyService from "../../services/company/subcompanyService";

function SubCompanies() {
  const subcompanyTableRef = React.createRef();
  const [subcompanies, setSubcompanies] = useState([]);
  const [newSubcompany, setNewSubcompany] = useState({ sub_company_code: "", sub_company_name: "", is_default: false });
  const [editingSubcompany, setEditingSubcompany] = useState(null);
  const [isAddSubcompanyFormOpen, setIsAddSubcompanyFormOpen] = useState(false);
  const [isEditSubcompanyFormOpen, setIsEditSubcompanyFormOpen] = useState(false);
  const [isSubcompanyLoading, setIsSubcompanyLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSubcompanies();
  }, []);

  const fetchSubcompanies = () => {
    setIsSubcompanyLoading(true);
    subcompanyService.getSubcompany().then((response) => {
      setSubcompanies(response.data);
      setIsSubcompanyLoading(false);
    }).catch(err => {
      toast.error(t("Failed to fetch companies"), { position: "top-right", autoClose: 2000, theme: "light" });
      setIsSubcompanyLoading(false);
    });
  };

  const addSubcompany = async () => {
    if (!newSubcompany.sub_company_code.trim() || !newSubcompany.sub_company_name.trim()) {
      setError(t("company code and name are required"));
      return;
    }
    setIsSubcompanyLoading(true);
    try {
      const response = await subcompanyService.createSubcompany(newSubcompany);
      setSubcompanies([...subcompanies, response.data]);
      setNewSubcompany({ sub_company_code: "", sub_company_name: "", is_default: false });
      setIsAddSubcompanyFormOpen(false);
      setError(null);
      setIsSubcompanyLoading(false);
      toast.success(t("company added successfully"), { position: "top-right", autoClose: 2000, theme: "light" });
    } catch (err) {
      setError(t("Failed to add company"));
      setIsSubcompanyLoading(false);
      toast.error(t("Failed to add company"), { position: "top-right", autoClose: 2000, theme: "light" });
    }
  };

  const editSubcompany = (subcompany) => {
    setEditingSubcompany(subcompany);
    setIsEditSubcompanyFormOpen(true);
    setIsAddSubcompanyFormOpen(false);
  };

  const saveEditedSubcompany = async () => {
    if (!editingSubcompany?.sub_company_code.trim() || !editingSubcompany?.sub_company_name.trim()) {
      setError(t("company code and name are required"));
      return;
    }
    setIsSubcompanyLoading(true);
    try {
      const response = await subcompanyService.updateSubcompany(editingSubcompany.id, editingSubcompany);
      setSubcompanies(subcompanies.map((sub) => sub.id === editingSubcompany.id ? response.data : sub));
      setIsEditSubcompanyFormOpen(false);
      setEditingSubcompany(null);
      setError(null);
      setIsSubcompanyLoading(false);
      toast.success(t("company updated successfully"), { position: "top-right", autoClose: 2000, theme: "light" });
    } catch (err) {
      setError(t("Failed to update company"));
      setIsSubcompanyLoading(false);
      toast.error(t("Failed to update company"), { position: "top-right", autoClose: 2000, theme: "light" });
    }
  };

  const removeSubcompany = (id) => {
    confirmAlert({
      title: '',
      message: 'Are you sure you want to delete this Company?',
      buttons: [
        {
          label: 'Yes',
          onClick: async () => {
            setIsSubcompanyLoading(true);

            try {
              await subcompanyService.deleteSubcompany(id);

              // Optimistic UI update
              setSubcompanies(prev =>
                prev.filter(sub => sub.id !== id)
              );

              toast.success(t("Sub Company deleted successfully"), {
                position: "top-right",
                autoClose: 2000,
                theme: "light",
              });

            } catch (err) {
              console.error("Sub Company delete failed:", err);

              toast.error(t("Failed to delete company"), {
                position: "top-right",
                autoClose: 2000,
                theme: "light",
              });

            } finally {
              setIsSubcompanyLoading(false);
            }
          },
        },
        {
          label: 'No',
          onClick: () => { },
        },
      ],
    });
  };


  return (
    <div className="container-fluid mt-3">
      <ToastContainer />
      <div className="card mt-3">
        <div className="card-header d-flex justify-content-between align-items-center p-3">
          <div>
            <div className="text-lg flex items-center gap-2 text-dark">
              <Building2 className="w-5 h-5 mr-2" />
              {t("Subcompanies")}
            </div>
            <p className="text-sm text-gray-600">{t("Manage your companies and branches")}</p>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => {
              setIsAddSubcompanyFormOpen(!isAddSubcompanyFormOpen);
              setIsEditSubcompanyFormOpen(false);
              setNewSubcompany({ sub_company_code: "", sub_company_name: "", is_default: false });
              setEditingSubcompany(null);
              setError(null);
            }}
            disabled={isSubcompanyLoading}
          >
            <Plus className="w-11 h-11 mr-2" />
            {t("Add Company")}
          </button>
        </div>
        <div className="card-body p-3">
          {isSubcompanyLoading && !subcompanies.length ? (
            <ContentLoader viewBox="0 0 400 100" backgroundColor="rgba(0,0,0,0.06)" foregroundColor="rgba(0,0,0,0.12)">
              <rect x="0" y="10" rx="4" ry="4" width="400" height="20" />
              <rect x="0" y="40" rx="4" ry="4" width="400" height="20" />
              <rect x="0" y="70" rx="4" ry="4" width="400" height="20" />
            </ContentLoader>
          ) : (
            <>
              <MaterialTable
                tableRef={subcompanyTableRef}
                style={{ backgroundColor: "var(--bs-table-bg)", borderBottomWidth: "1px", boxShadow: "inset 0 0 0 9999px var(--bs-table-accent-bg)" }}
                title=""
                columns={[
                  { title: t("Subcompany Name"), field: "sub_company_name", render: rowData => (<div>{rowData.sub_company_name}</div>), headerStyle: { fontWeight: "bold" } },
                  { title: t("Subcompany Code"), field: "sub_company_code", render: rowData => (<div>{rowData.sub_company_code}</div>), headerStyle: { fontWeight: "bold" } },
                  { title: t("Default"), field: "is_default", render: rowData => (<div>{rowData.is_default ? 'Yes' : 'No'}</div>), headerStyle: { fontWeight: "bold" } },
                ]}
                data={subcompanies}
                actions={[
                  { icon: () => <img src={process.env.PUBLIC_URL + "/static/images/ic-edit.svg"} alt="edit" />, tooltip: t("Edit"), onClick: (event, rowData) => editSubcompany(rowData) },
                  { icon: () => <img src={process.env.PUBLIC_URL + "/static/images/ic-delete.svg"} alt="delete" />, tooltip: t("Delete"), onClick: (event, rowData) => removeSubcompany(rowData.id) },
                ]}
                options={{ headerStyle: { backgroundColor: "white", color: "#245C7F", fontSize: 11 }, rowStyle: (rowData, index) => ({ backgroundColor: index % 2 ? "#fff" : undefined }), paging: false, pageSize: 10, actionsColumnIndex: -1, toolbar: false }}
              />

              {(isAddSubcompanyFormOpen || isEditSubcompanyFormOpen) && (
                <div className="mt-5">
                  <h4 className="text-md">{isAddSubcompanyFormOpen ? t("Add New Company") : t("Edit Company")}</h4>
                  <div>{isAddSubcompanyFormOpen ? t("Enter the name for the new Company or branch.") : t("Update the company name.")}</div>
                  <div className="row mt-3">
                    <div className="col-md-4">
                      <label htmlFor="sub-code" className="col-form-label">
                        {t("Subcompany Code")} <Tooltip content={t("Suncompanycode_Tooltip")}><i className="fa fa-light fa-circle-info"></i></Tooltip>
                      </label>
                      <input id="sub-code" className="form-control w-full border rounded-md p-2" value={isAddSubcompanyFormOpen ? newSubcompany.sub_company_code : editingSubcompany?.sub_company_code} onChange={(e) => isAddSubcompanyFormOpen ? setNewSubcompany({ ...newSubcompany, sub_company_code: e.target.value }) : setEditingSubcompany({ ...editingSubcompany, sub_company_code: e.target.value })} placeholder={t("Enter company code")} />
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="sub-name" className="col-form-label">
                        {t("Subcompany Name")} <Tooltip content={t("Suncompanyname_Tooltip")}><i className="fa fa-light fa-circle-info"></i></Tooltip>
                      </label>
                      <input id="sub-name" className="form-control w-full border rounded-md p-2" value={isAddSubcompanyFormOpen ? newSubcompany.sub_company_name : editingSubcompany?.sub_company_name} onChange={(e) => isAddSubcompanyFormOpen ? setNewSubcompany({ ...newSubcompany, sub_company_name: e.target.value }) : setEditingSubcompany({ ...editingSubcompany, sub_company_name: e.target.value })} placeholder={t("Enter company name")} />
                    </div>
                    <div className="col-md-4 d-flex align-items-end">
                      <div className="w-100">
                        <label htmlFor="sub-default" className="col-form-label">
                          <input id="sub-default" type="checkbox" checked={isAddSubcompanyFormOpen ? newSubcompany.is_default : editingSubcompany?.is_default} onChange={(e) => isAddSubcompanyFormOpen ? setNewSubcompany({ ...newSubcompany, is_default: e.target.checked }) : setEditingSubcompany({ ...editingSubcompany, is_default: e.target.checked })} />
                          <span className="ml-2">{t("Set as Default")} <Tooltip content={t("setasdefault_Tooltip")}><i className="fa fa-light fa-circle-info"></i></Tooltip></span>
                        </label>
                      </div>
                    </div>
                  </div>
                  {error && <div className="text-red-500 text-sm mt-2">{error}</div>}
                  <div className="mt-3">
                    <button className="btn btn-secondary" onClick={() => { setIsAddSubcompanyFormOpen(false); setIsEditSubcompanyFormOpen(false); }}>{t("Cancel")}</button>
                    <button className="ml-2 btn btn-primary" onClick={isAddSubcompanyFormOpen ? addSubcompany : saveEditedSubcompany}>{isSubcompanyLoading ? "Saving..." : (isAddSubcompanyFormOpen ? t("Save") : t("Update"))}</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default SubCompanies;